import { motion } from "framer-motion";
import { EyeOff, Inbox, KeyRound, Loader2, Save, UserCircle2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import AvatarUploader from "../components/common/AvatarUploader";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ROLES } from "../lib/constants";
import { effectiveRoles } from "../lib/roles";

export default function Profile() {
    const { user, refresh } = useAuth();
    const [name, setName] = useState(user?.name || "");
    const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || "");
    const [phone, setPhone] = useState(user?.phone || "");
    const [password, setPassword] = useState("");
    const [passwordConfirm, setPasswordConfirm] = useState("");
    const [submitting, setSubmitting] = useState(false);
    // CEO 2026-09-30 · Préférence copie notification (CC/BCC + email alt).
    const [notifEmail, setNotifEmail] = useState(user?.notification_cc_email || "");
    const [notifMode, setNotifMode] = useState(user?.notification_cc_mode || "cc");
    const [savingNotif, setSavingNotif] = useState(false);

    if (!user) return null;

    async function saveProfile() {
        if (!name.trim()) { toast.error("Nom requis"); return; }
        setSubmitting(true);
        try {
            await api.patch("/users/me/profile", {
                name: name.trim(),
                avatar_url: avatarUrl || null,
                phone: (phone || "").trim() || null,
            });
            await refresh();
            toast.success("Profil mis à jour");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        } finally {
            setSubmitting(false);
        }
    }

    async function savePassword() {
        if (password.length < 8) { toast.error("Mot de passe : 8 caractères minimum"); return; }
        if (password !== passwordConfirm) { toast.error("Les mots de passe ne correspondent pas"); return; }
        setSubmitting(true);
        try {
            await api.patch("/users/me/profile", { password });
            toast.success("Mot de passe mis à jour");
            setPassword("");
            setPasswordConfirm("");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        } finally {
            setSubmitting(false);
        }
    }

    async function saveNotifPref() {
        const trimmed = (notifEmail || "").trim();
        if (trimmed && !trimmed.includes("@")) {
            toast.error("Email de notification invalide (doit contenir un @)");
            return;
        }
        setSavingNotif(true);
        try {
            await api.patch("/users/me/profile", {
                notification_cc_email: trimmed,
                notification_cc_mode: notifMode,
            });
            await refresh();
            toast.success(
                "Préférence enregistrée",
                {
                    description: notifMode === "bcc"
                        ? `Vos copies seront envoyées en BCC à ${trimmed || user.email} — invisible du candidat.`
                        : `Vos copies seront envoyées en CC à ${trimmed || user.email}.`,
                },
            );
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        } finally {
            setSavingNotif(false);
        }
    }

    const rolesList = Array.from(effectiveRoles(user));

    return (
        <div className="max-w-3xl mx-auto space-y-6" data-testid="profile-page">
            <header>
                <div className="inline-flex items-center gap-2 rounded-full bg-shariti-orange-soft text-shariti-orange px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
                    <UserCircle2 className="h-3 w-3" /> Mon compte
                </div>
                <h1 className="mt-3 text-3xl md:text-4xl font-black tracking-tight text-shariti-black">
                    Mon profil
                </h1>
                <p className="mt-2 text-sm font-medium text-slate-500">
                    Personnalisez votre nom, votre photo (importée depuis votre ordinateur ou téléphone) et votre mot de passe.
                </p>
            </header>

            <motion.section
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="card-soft p-6 md:p-8 space-y-5"
                data-testid="profile-section-identity"
            >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <div className="text-xs font-medium text-slate-500">{user.email}</div>
                        <div className="mt-1 flex flex-wrap gap-1.5">
                            {rolesList.map((r) => (
                                <span key={r} className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-shariti-orange-soft text-shariti-orange">
                                    {ROLES[r]?.label || r}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>

                <div>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-3 block">
                        Photo de profil
                    </span>
                    <AvatarUploader
                        value={avatarUrl}
                        name={name || user.name}
                        onChange={setAvatarUrl}
                        size={104}
                        testId="profile-avatar"
                    />
                </div>

                <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5 block">Nom complet</span>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        data-testid="profile-input-name"
                        className="input-shariti"
                    />
                </label>

                <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5 block">Téléphone</span>
                    <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="ex. 06 12 34 56 78"
                        autoComplete="tel"
                        data-testid="profile-input-phone"
                        className="input-shariti"
                    />
                    <span className="mt-1.5 block text-[10px] font-medium text-slate-400">
                        Si vous êtes Référent, ce numéro est inséré automatiquement dans le SMS récap envoyé au candidat pour qu'il puisse vous joindre sur place.
                    </span>
                </label>

                <div className="flex justify-end">
                    <button
                        onClick={saveProfile}
                        disabled={submitting}
                        data-testid="profile-save"
                        className="btn-shariti disabled:opacity-60"
                    >
                        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        Enregistrer le profil
                    </button>
                </div>
            </motion.section>

            <motion.section
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.03 }}
                className="card-soft p-6 md:p-8 space-y-4"
                data-testid="profile-section-notif"
            >
                <div className="flex items-center gap-2">
                    <Inbox className="h-4 w-4 text-shariti-orange" />
                    <h2 className="text-lg font-extrabold tracking-tight text-shariti-black">
                        Copies des emails envoyés aux candidats
                    </h2>
                </div>
                <p className="text-xs font-medium text-slate-500">
                    Quand vous envoyez une lettre officielle à un candidat (contrat, rupture de période d'essai, convocation…), vous recevez automatiquement une copie pour garder une trace dans votre boîte. Personnalisez où cette copie arrive et si elle est <strong>visible du candidat</strong> ou pas.
                </p>

                {/* Mode : CC vs BCC */}
                <div>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2 block">
                        Mode d'envoi de votre copie
                    </span>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                        <button
                            type="button"
                            onClick={() => setNotifMode("cc")}
                            data-testid="profile-notif-mode-cc"
                            className={`text-left p-3 rounded-2xl border-2 transition ${
                                notifMode === "cc"
                                    ? "bg-shariti-orange-soft border-shariti-orange"
                                    : "bg-white border-shariti-gray hover:border-shariti-orange/40"
                            }`}
                        >
                            <div className="flex items-center gap-2 mb-1">
                                <div className={`h-4 w-4 rounded-full border-2 flex items-center justify-center ${notifMode === "cc" ? "bg-shariti-orange border-shariti-orange" : "bg-white border-shariti-gray"}`}>
                                    {notifMode === "cc" && <div className="h-1.5 w-1.5 rounded-full bg-white" />}
                                </div>
                                <span className="text-sm font-extrabold text-shariti-black">
                                    En copie visible (CC)
                                </span>
                            </div>
                            <p className="text-[11px] font-medium text-slate-500 leading-snug">
                                Le candidat voit votre adresse dans les destinataires de la copie — comportement par défaut, utile pour les échanges collaboratifs.
                            </p>
                        </button>
                        <button
                            type="button"
                            onClick={() => setNotifMode("bcc")}
                            data-testid="profile-notif-mode-bcc"
                            className={`text-left p-3 rounded-2xl border-2 transition ${
                                notifMode === "bcc"
                                    ? "bg-indigo-50 border-indigo-500"
                                    : "bg-white border-shariti-gray hover:border-indigo-300"
                            }`}
                        >
                            <div className="flex items-center gap-2 mb-1">
                                <div className={`h-4 w-4 rounded-full border-2 flex items-center justify-center ${notifMode === "bcc" ? "bg-indigo-600 border-indigo-600" : "bg-white border-shariti-gray"}`}>
                                    {notifMode === "bcc" && <div className="h-1.5 w-1.5 rounded-full bg-white" />}
                                </div>
                                <EyeOff className="h-3.5 w-3.5 text-indigo-500" />
                                <span className="text-sm font-extrabold text-shariti-black">
                                    En copie cachée (BCC)
                                </span>
                            </div>
                            <p className="text-[11px] font-medium text-slate-500 leading-snug">
                                Vous recevez la copie dans votre boîte mais <strong>le candidat ne voit pas</strong> votre adresse — recommandé pour préserver votre email personnel.
                            </p>
                        </button>
                    </div>
                </div>

                {/* Email alternatif */}
                <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5 block">
                        Adresse email de notification (optionnel)
                    </span>
                    <input
                        type="email"
                        value={notifEmail}
                        onChange={(e) => setNotifEmail(e.target.value)}
                        placeholder={`Par défaut : ${user.email}`}
                        data-testid="profile-notif-email"
                        autoComplete="email"
                        className="input-shariti"
                    />
                    <span className="mt-1.5 block text-[10px] font-medium text-slate-400">
                        Laissez vide pour utiliser votre email de connexion. Mettez une adresse générique (ex. <code className="font-mono text-shariti-orange">rh@shariti.com</code>) pour éviter que votre email personnel soit visible dans les lettres envoyées aux candidats.
                    </span>
                </label>

                <div className="flex items-center justify-between gap-3 pt-2 flex-wrap">
                    <div className="text-[11px] font-semibold text-slate-500 bg-shariti-cream/60 rounded-xl px-3 py-2 flex-1 min-w-[200px]">
                        {notifMode === "bcc" ? (
                            <>→ Vous recevrez les copies <strong>en BCC</strong> à <strong>{(notifEmail || "").trim() || user.email}</strong> — invisible du candidat.</>
                        ) : (
                            <>→ Vous recevrez les copies <strong>en CC visible</strong> à <strong>{(notifEmail || "").trim() || user.email}</strong>.</>
                        )}
                    </div>
                    <button
                        onClick={saveNotifPref}
                        disabled={savingNotif}
                        data-testid="profile-save-notif"
                        className="btn-shariti disabled:opacity-50"
                    >
                        {savingNotif ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        Enregistrer la préférence
                    </button>
                </div>
            </motion.section>

            <motion.section
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
                className="card-soft p-6 md:p-8 space-y-4"
                data-testid="profile-section-password"
            >
                <div className="flex items-center gap-2">
                    <KeyRound className="h-4 w-4 text-shariti-orange" />
                    <h2 className="text-lg font-extrabold tracking-tight text-shariti-black">Mot de passe</h2>
                </div>
                <p className="text-xs font-medium text-slate-500">
                    Minimum 8 caractères. Laissez vide pour conserver l'actuel.
                </p>
                <div className="grid md:grid-cols-2 gap-3">
                    <label className="block">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5 block">Nouveau mot de passe</span>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="••••••••"
                            data-testid="profile-input-password"
                            autoComplete="new-password"
                            className="input-shariti"
                        />
                    </label>
                    <label className="block">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5 block">Confirmer</span>
                        <input
                            type="password"
                            value={passwordConfirm}
                            onChange={(e) => setPasswordConfirm(e.target.value)}
                            placeholder="••••••••"
                            data-testid="profile-input-password-confirm"
                            autoComplete="new-password"
                            className="input-shariti"
                        />
                    </label>
                </div>
                <div className="flex justify-end">
                    <button
                        onClick={savePassword}
                        disabled={submitting || !password}
                        data-testid="profile-save-password"
                        className="btn-shariti disabled:opacity-50"
                    >
                        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                        Mettre à jour le mot de passe
                    </button>
                </div>
            </motion.section>
        </div>
    );
}
